import {
  ClassificationValues,
  DecisionValues,
  StatementStatusValues,
  classificationEnum,
  decisionEnum,
  statementStatusEnum,
} from './models'

export const wizardStepEnum = {
  UPLOAD: 'upload',
  ACCOUNT: 'account',
  COLUMNS: 'columns',
  MATCHED: 'matched',
  UNMATCHED: 'unmatched',
  FINISH: 'finish',
} as const

export type WizardStep = (typeof wizardStepEnum)[keyof typeof wizardStepEnum]

// i18n keys for the step indicator labels.
export const wizardStepLabelKeys: Record<WizardStep, string> = {
  [wizardStepEnum.UPLOAD]: 'statementReview.steps.upload',
  [wizardStepEnum.ACCOUNT]: 'statementReview.steps.account',
  [wizardStepEnum.COLUMNS]: 'statementReview.steps.columns',
  [wizardStepEnum.MATCHED]: 'statementReview.steps.matched',
  [wizardStepEnum.UNMATCHED]: 'statementReview.steps.unmatched',
  [wizardStepEnum.FINISH]: 'statementReview.steps.finish',
}

// Minimal structural inputs so the derivation stays pure and trivially testable.
// Note there is no AI flag here: the step is a function of state alone, so the
// same status + column map + rows yield the same step whether the map was
// alias-matched, AI-prefilled, or hand-entered. `columnMap` is present for every
// tabular (csv/xlsx/xls) statement — covering every raw header, regardless of
// whether the alias table, AI, or neither resolved it — and absent only for a
// PDF-derived statement, which has no raw headers to map and never gets a
// Columns step.
export interface WizardStatement {
  status: StatementStatusValues
  columnMap?: Record<string, unknown> | null
  columnMapConfirmed?: boolean | null
  // The account is settled in the Account step. A suggestion alone never
  // confirms it; an account picked at upload arrives already confirmed.
  paymentAccount?: string | null
  paymentAccountConfirmed?: boolean
}

export interface WizardRow {
  classification?: ClassificationValues | null
  decision?: DecisionValues
}

// Statuses that always resolve to the Upload step, regardless of column map.
// `ready` is deliberately excluded: for a tabular statement that's exactly the
// "normalized, awaiting Columns approval" state (see `statementHasColumnMap`
// below), while a PDF-derived `ready` (no column map, auto-chains instantly)
// falls through to the same Upload result further down. `failed` lives here
// too: the failure happens during processing, so the user lands back on Upload.
const UPLOAD_STATUSES: StatementStatusValues[] = [
  statementStatusEnum.UPLOADED,
  statementStatusEnum.PROCESSING,
  statementStatusEnum.RECONCILING,
  statementStatusEnum.FAILED,
]

export function statementHasColumnMap(statement: WizardStatement): boolean {
  return Boolean(statement.columnMap && Object.keys(statement.columnMap).length > 0)
}

export function statementNeedsAccount(statement: WizardStatement): boolean {
  return !statement.paymentAccount || statement.paymentAccountConfirmed === false
}

function hasMatchedRows(rows: WizardRow[]): boolean {
  return rows.some((r) => r.classification === classificationEnum.MATCHED)
}

function hasPendingMatchedRows(rows: WizardRow[]): boolean {
  return rows.some(
    (r) => r.classification === classificationEnum.MATCHED && r.decision === decisionEnum.PENDING,
  )
}

/**
 * Steps that appear in the indicator for this statement. Account appears for
 * every statement, right after Upload. Columns appears for
 * every tabular statement (a column map exists); once confirmed it remains as
 * a completed step. A PDF-derived statement has no column map and never shows
 * it. Matched appears only when at least one row matched.
 */
export function getApplicableSteps(statement: WizardStatement, rows: WizardRow[]): WizardStep[] {
  const steps: WizardStep[] = [wizardStepEnum.UPLOAD, wizardStepEnum.ACCOUNT]
  if (statementHasColumnMap(statement)) steps.push(wizardStepEnum.COLUMNS)
  if (hasMatchedRows(rows)) steps.push(wizardStepEnum.MATCHED)
  steps.push(wizardStepEnum.UNMATCHED)
  steps.push(wizardStepEnum.FINISH)
  return steps
}

/**
 * The step the statement's current state implies. Pure: same status + rows
 * always yields the same step, so reload / back / list re-entry all agree.
 */
export function deriveCurrentStep(statement: WizardStatement, rows: WizardRow[]): WizardStep {
  const { status } = statement
  if (status === statementStatusEnum.CONFIRMED) return wizardStepEnum.FINISH
  if (UPLOAD_STATUSES.includes(status)) return wizardStepEnum.UPLOAD
  // Out of the upload statuses, nothing else can proceed until an account is
  // confirmed: reconciliation's candidate expenses depend on it.
  if (statementNeedsAccount(statement)) return wizardStepEnum.ACCOUNT
  // ready or in_review: Columns is the active step until the map is confirmed
  // — whether it was fully alias-matched, AI-prefilled, or still awaits manual
  // mapping. A tabular statement stops at `ready` for exactly this reason: no
  // reconciliation runs until Columns is approved. A PDF-derived statement has
  // no columnMap at all, so this falls straight through for it.
  if (statementHasColumnMap(statement) && statement.columnMapConfirmed !== true) {
    return wizardStepEnum.COLUMNS
  }
  if (status === statementStatusEnum.READY) return wizardStepEnum.UPLOAD
  if (hasPendingMatchedRows(rows)) return wizardStepEnum.MATCHED
  return wizardStepEnum.UNMATCHED
}

export interface WizardState {
  steps: WizardStep[]
  currentStep: WizardStep
  derivedStep: WizardStep
  canGoBack: boolean
  canGoNext: boolean
}

/**
 * Resolve the active step from derivation plus an optional URL-requested step.
 * A requested step is honored only when it is applicable and not ahead of the
 * derived step — you may revisit earlier steps but never skip ahead of state.
 */
export function resolveWizardState(
  statement: WizardStatement,
  rows: WizardRow[],
  requestedStep?: string | null,
): WizardState {
  const steps = getApplicableSteps(statement, rows)
  const derivedStep = deriveCurrentStep(statement, rows)
  let maxIndex = steps.indexOf(derivedStep)

  // While reviewing, the user may always advance to Finish to complete once the
  // gating steps are behind them — i.e. once derivation has settled on Unmatched
  // (Columns confirmed, no pending Matched rows). Unmatched rows may remain.
  if (
    statement.status === statementStatusEnum.IN_REVIEW &&
    derivedStep === wizardStepEnum.UNMATCHED
  ) {
    maxIndex = steps.indexOf(wizardStepEnum.FINISH)
  }

  let currentStep = derivedStep
  if (requestedStep) {
    const reqIndex = steps.indexOf(requestedStep as WizardStep)
    if (reqIndex !== -1 && reqIndex <= maxIndex) {
      currentStep = steps[reqIndex]
    }
  }

  const currentIndex = steps.indexOf(currentStep)
  return {
    steps,
    currentStep,
    derivedStep,
    canGoBack: currentIndex > 0,
    canGoNext: currentIndex < maxIndex,
  }
}
